<script lang="ts" setup>
import SearchIcon from '@/components/common/Search/icon/SearchIcon.vue';
import ClearIcon from './icon/ClearIcon.vue';
import DividerIcon from './icon/DividerIcon.vue';
import { ref, computed } from 'vue';

const props = defineProps<{
    initial?: string;
    mode: 'home' | 'result';
    allowDuplicate?: boolean; // 是否允许重复搜索相同内容
}>();

const emit = defineEmits<{
    search: [query: string];
}>();

const searchQuery = ref('');
// 最近一次已触发的 "规范化查询词", 用于去重
let lastEmittedQuery: string | undefined = undefined;

const searchInputRef = ref<HTMLInputElement | null>(null);
const isInputFocused = ref(false);

// 计算显示逻辑
const showElements = computed(() => {
    const hasContent = !!searchQuery.value;
    const isHome = props.mode === 'home';
    const isResult = props.mode === 'result';

    if (isHome) {
        // Home 页逻辑
        return {
            searchIcon: true,
            searchInput: true,
            clearIcon: hasContent,
            divider: hasContent,
            searchButton: true,
        };
    } else if (isResult) {
        // Result 页逻辑
        return {
            searchIcon: true,
            searchInput: true,
            clearIcon: hasContent,
            divider: hasContent && isInputFocused.value,
            searchButton: isInputFocused.value,
        };
    }

    // 默认显示所有元素
    return {
        searchIcon: true,
        searchInput: true,
        clearIcon: hasContent,
        divider: hasContent,
        searchButton: true,
    };
});

const handleSearch = () => {
    const normalized = searchQuery.value.trim();
    console.log('[handleSearch]', { normalized, lastEmittedQuery });

    // 根据 allowDuplicate 控制是否进行去重检查
    if (!props.allowDuplicate && normalized === lastEmittedQuery) return;

    lastEmittedQuery = normalized;
    emit('search', normalized);
};

const handleClear = (e: MouseEvent) => {
    searchQuery.value = '';
    // 阻止默认行为, 避免焦点竞争
    e.preventDefault();
    searchInputRef.value?.focus();
};

const handleInputFocus = () => {
    isInputFocused.value = true;
};

const handleInputBlur = () => {
    isInputFocused.value = false;
};

// 初始值策略: 若提供 initial (即使为空字符串), 会在挂载时触发一次
if (props.initial !== undefined) {
    console.log('[initial]', props.initial);

    searchQuery.value = props.initial;
    handleSearch();
}
</script>

<template>
    <div
        class="search-container"
        :class="{ [`search-container--${mode}`]: true }"
    >
        <!-- 搜索图标 - 始终显示 -->
        <div v-show="showElements.searchIcon" class="icon-wrapper search-icon">
            <SearchIcon />
        </div>

        <!-- 搜索输入框 - 始终显示 -->
        <input
            v-show="showElements.searchInput"
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            placeholder="Fresh Finds"
            class="search-input"
            @keyup.enter="handleSearch"
            @focus="handleInputFocus"
            @blur="handleInputBlur"
            maxlength="100"
        />

        <!-- 清除图标 - 根据模式和内容显示 -->
        <div
            v-show="showElements.clearIcon"
            class="icon-wrapper clear-icon"
            @mousedown="handleClear"
        >
            <ClearIcon />
        </div>

        <!-- 分割线 - 根据模式和状态显示 -->
        <div v-show="showElements.divider" class="divider-wrapper">
            <DividerIcon />
        </div>

        <!-- 搜索按钮 - 根据模式和状态显示 -->
        <!-- 
            当用户点击按钮时, 输入框会失去焦点, 导致按钮瞬间隐藏, 从而无法触发点击事件, 这是一个经典的焦点竞争问题.
            使用 mousedown 可以避免焦点竞争.
        -->
        <button
            v-show="showElements.searchButton"
            class="search-button"
            @mousedown="handleSearch"
            type="button"
        >
            Search
        </button>
    </div>
</template>

<style lang="scss" scoped>
@use './Search.scss';
</style>
